import React, { useMemo, useState } from 'react';
import { addMonths, eachDayOfInterval, endOfMonth, format, isSameDay, isWeekend, max as maxDate, min as minDate, parseISO, startOfMonth } from 'date-fns';
import { CalendarDaysIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { timeOffEntries } from '../../data/timeOff';
import type { EntryTone, Navigate, PtoRequest } from '../../types/hr';
import { displayName, getEmployee } from '../../utils/employees';
import { TODAY, formatDays, formatRange } from '../../utils/format';
import { overlaps, policyName } from '../../utils/timeOff';
import { Avatar } from '../ui/Avatar';
import { Card } from '../ui/Card';
import { EmptyState } from '../ui/EmptyState';
interface WhosOutCalendarProps {
  requests: PtoRequest[];
  onNavigate: Navigate;
}
interface CalendarItem {
  id: string;
  employeeId: string;
  start: string;
  end: string;
  days: number;
  label: string;
  tone: EntryTone;
  pending: boolean;
}
const BAR_TONES: Record<EntryTone, string> = {
  blue: 'bg-azure text-white',
  teal: 'bg-deep text-white',
  navy: 'bg-navy text-white',
  orange: 'bg-flame text-white'
};
export function WhosOutCalendar({
  requests,
  onNavigate
}: WhosOutCalendarProps) {
  const [month, setMonth] = useState(startOfMonth(TODAY));
  const monthEnd = endOfMonth(month);
  const days = eachDayOfInterval({
    start: month,
    end: monthEnd
  });
  const items: CalendarItem[] = useMemo(() => [...timeOffEntries.map(e => ({
    id: e.id,
    employeeId: e.employeeId,
    start: e.start,
    end: e.end,
    days: e.days,
    label: e.policyLabel,
    tone: e.tone,
    pending: false
  })), ...requests.filter(r => r.status !== 'denied').map(r => ({
    id: r.id,
    employeeId: r.employeeId,
    start: r.start,
    end: r.end,
    days: r.days,
    label: policyName(r.policyId),
    tone: (r.policyId === 'newhire' ? 'navy' : 'blue') as EntryTone,
    pending: r.status === 'pending'
  }))], [requests]);
  const rows = useMemo(() => {
    const inMonth = items.filter(i => overlaps(i, month, monthEnd));
    const byEmployee = new Map<string, CalendarItem[]>();
    inMonth.forEach(i => byEmployee.set(i.employeeId, [...(byEmployee.get(i.employeeId) ?? []), i]));
    return Array.from(byEmployee.entries()).sort((a, b) => a[1][0].start.localeCompare(b[1][0].start));
  }, [items, month, monthEnd]);
  const grid = {
    gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))`
  };
  return <Card title={format(month, 'MMMM yyyy')} subtitle={`${rows.length} people with time off`} icon={CalendarDaysIcon} action={<div className="flex items-center gap-1" data-id="element-701" data-mp="1meh5u4knb3uj">
          <button type="button" onClick={() => setMonth(startOfMonth(TODAY))} className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-azure hover:bg-azure/10" data-id="element-702" data-mp="1mfs7nyuj61j0">
            Today
          </button>
          <button type="button" onClick={() => setMonth(addMonths(month, -1))} aria-label="Previous month" className="rounded-lg p-1.5 text-navy hover:bg-canvas" data-id="element-703" data-mp="1chm7blodh3g9">
            <ChevronLeftIcon className="h-4 w-4" data-id="element-704" data-mp="3t2820wq5etka" />
          </button>
          <button type="button" onClick={() => setMonth(addMonths(month, 1))} aria-label="Next month" className="rounded-lg p-1.5 text-navy hover:bg-canvas" data-id="element-705" data-mp="3t4ki14ccitup">
            <ChevronRightIcon className="h-4 w-4" data-id="element-706" data-mp="5wqjtfjq4hez" />
          </button>
        </div>} bodyClassName="pb-5" data-id="element-700" data-mp="1ve462n1n23m0">
      {rows.length === 0 ? <div className="px-6" data-id="element-707" data-mp="3ac9k7owjtm3g">
          <EmptyState icon={CalendarDaysIcon} title="No time off this month" data-id="element-708" data-mp="3abghr04mhir4" />
        </div> : <div className="overflow-x-auto" data-id="element-709" data-mp="3ecsdlne3b07r">
          <div className="min-w-[960px] px-6" data-id="element-710" data-mp="3ebqqulnpopa7">
            <div className="flex border-b border-slate-100 pb-2" data-id="element-711" data-mp="3j4tjmrknzupc">
              <div className="w-52 shrink-0" data-id="element-712" data-mp="2mmnjgx0j2ytc" />
              <div className="grid flex-1" style={grid} data-id="element-713" data-mp="2mlvzykk7grjf">
                {days.map(d => {
              const isToday = isSameDay(d, TODAY);
              return <div key={d.toISOString()} className="flex flex-col items-center gap-0.5" data-id="element-714" data-mp="27iulfw74gyta">
                      <span className="text-[9px] font-medium uppercase text-mutedlight" data-id="element-715" data-mp="22l2r6vj090lx">{format(d, 'EEEEE')}</span>
                      <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-medium ${isToday ? 'bg-flame text-white' : isWeekend(d) ? 'text-slate-300' : 'text-slate-600'}`} data-id="element-716" data-mp="2haig05yz6vyz">
                        {format(d, 'd')}
                      </span>
                    </div>;
            })}
              </div>
            </div>

            {rows.map(([employeeId, entries]) => {
          const emp = getEmployee(employeeId);
          if (!emp) return null;
          return <div key={employeeId} className="group flex items-center border-b border-slate-50 last:border-0" data-id="element-717" data-mp="15w4ydiutfcre">
                  <button type="button" onClick={() => onNavigate({
              page: 'profile',
              employeeId,
              tab: 'timeoff'
            })} className="flex w-52 shrink-0 items-center gap-2.5 py-2.5 pr-3 text-left" data-id="element-718" data-mp="3cikd58dy9w7k">
                    <Avatar name={displayName(emp)} seed={emp.id} size="sm" data-id="element-719" data-mp="32shfuvzspw3f" />
                    <span className="min-w-0" data-id="element-720" data-mp="37qj5jzrq8tnv">
                      <span className="block truncate text-sm font-medium text-navy group-hover:text-azure" data-id="element-721" data-mp="2ts2q9kfstuu4">{displayName(emp)}</span>
                      <span className="block truncate text-[11px] text-muted" data-id="element-722" data-mp="2xuplzwflbzwl">{emp.department}</span>
                    </span>
                  </button>
                  <div className="relative h-11 flex-1" data-id="element-723" data-mp="2jw844fcd9hml">
                    <div className="absolute inset-0 grid" style={grid} aria-hidden="true" data-id="element-724" data-mp="2otrf96drpeab">
                      {days.map(d => <div key={d.toISOString()} className={`border-l border-slate-100/70 ${isWeekend(d) ? 'bg-canvas' : ''} ${isSameDay(d, TODAY) ? 'bg-flame/[0.06]' : ''}`} data-id="element-725" data-mp="i0pbe01uqj6j" />)}
                    </div>
                    <div className="absolute inset-0 grid items-center" style={grid} data-id="element-726" data-mp="gnpjsa6qfb7c">
                      {entries.map(entry => {
                  const s = maxDate([parseISO(entry.start), month]).getDate();
                  const e = minDate([parseISO(entry.end), monthEnd]).getDate();
                  return <div key={entry.id} title={`${entry.label} · ${formatRange(entry.start, entry.end)}${entry.pending ? ' (pending)' : ''}`} style={{
                    gridColumn: `${s} / ${e + 1}`,
                    gridRow: 1
                  }} className={`mx-0.5 flex h-7 items-center overflow-hidden rounded-lg px-2 text-[11px] font-medium shadow-sm ${entry.pending ? 'border-2 border-dashed border-flame bg-flame/10 text-[#c25a00]' : BAR_TONES[entry.tone]}`} data-id="element-727" data-mp="zbkv7nevgcxa">
                            <span className="truncate" data-id="element-728" data-mp="1ulgap2dtnltp">{entry.pending ? 'Pending' : formatDays(entry.days)}</span>
                          </div>;
                })}
                    </div>
                  </div>
                </div>;
        })}

            <div className="mt-4 flex flex-wrap items-center gap-5 text-xs text-muted" data-id="element-729" data-mp="29hfhm9p73fx9">
              <Legend className="bg-azure" label="Regular PTO" data-id="element-730" data-mp="2diqy9pq3cqtb" />
              <Legend className="bg-navy" label="New Hire PTO" data-id="element-731" data-mp="2iash5uhrlfsb" />
              <Legend className="bg-deep" label="Leave of Absence" data-id="element-732" data-mp="2ibak19yxms97" />
              <Legend className="border-2 border-dashed border-flame bg-flame/10" label="Pending request" data-id="element-733" data-mp="2n8k8onehyzvz" />
            </div>
          </div>
        </div>}
    </Card>;
}
function Legend({
  className,
  label
}: {
  className: string;
  label: string;
}) {
  return <span className="inline-flex items-center gap-2" data-id="element-734" data-mp="3iihrzevosfnm">
      <span className={`h-3 w-5 rounded ${className}`} aria-hidden="true" data-id="element-735" data-mp="3igwvhjptamn4" />
      {label}
    </span>;
}